if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          form: "form",
          inputId: "[name=id]",
          submitButton: '[name="add"]',
          errorWrapper: ".f-product-form__error-message-wrapper",
          customFields: ["[data-product-custom-field]"],
        };

        this.domNodes = queryDomNodes(this.selectors, this);

        this.form = this.domNodes.form;
        this.submitButton = this.domNodes.submitButton;
        this.domNodes.inputId.disabled = false;
        this.Notification = window.FoxTheme.Notification;
        this.notificationType = this.dataset.notificationType;
        this.customFields = document.querySelectorAll(
          this.selectors.customFields
        );
        this.cartDrawer = document.querySelector("cart-drawer");
        this.mainCart = document.querySelector("cart-items");
        this.type = this.dataset.type;

        this.variantIdInput = this.domNodes.inputId;

        this.syncTrackingColourProperty();
        this.syncTrackingAttributionProperties();
        this.form?.addEventListener("change", () => {
          this.syncTrackingColourProperty();
        });

        this.errorWrapper =
          this.type === "product-card"
            ? this.closest(".product-card")
            : this.domNodes.errorWrapper;

        this.form &&
          this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
      }

      syncTrackingColourProperty() {
        if (!this.form) return;

        const colour = this.getTrackingColour();
        let input = this.form.querySelector('[name="properties[_gtm_colour]"]');

        if (!colour) {
          input?.remove();
          return;
        }

        if (!input) {
          input = document.createElement("input");
          input.type = "hidden";
          input.name = "properties[_gtm_colour]";
          this.form.appendChild(input);
        }

        input.value = colour;
      }

      getTrackingProductHandle() {
        const productCard = this.closest(".product-card");
        const productLink = productCard?.querySelector("[data-gtm-product-handle]");
        if (productLink?.dataset.gtmProductHandle) {
          return productLink.dataset.gtmProductHandle;
        }

        const productInfo = this.closest("product-info[data-product-url]");
        const productUrl = productInfo?.dataset.productUrl || "";

        return productUrl
          .split("?")[0]
          .split("#")[0]
          .split("/")
          .filter(Boolean)
          .pop() || "";
      }

      getTrackingAttribution() {
        const tracking = window.ShopifyTracking;
        const productHandle = this.getTrackingProductHandle();
        if (!productHandle) return null;

        const stored = tracking?.getItemListAttribution?.(productHandle);
        if (stored) return stored;

        try {
          const raw = window.sessionStorage.getItem("analytics:view-item-attribution");
          const attribution = raw ? JSON.parse(raw) : null;
          const isCurrentProduct =
            attribution?.product_handle === String(productHandle).toUpperCase();
          const isCurrent =
            attribution && Date.now() - Number(attribution.timestamp || 0) <= 30 * 60 * 1000;

          if (isCurrentProduct && isCurrent) return attribution;
        } catch (error) {
          // Tracking must not interfere with adding a product to cart.
        }

        const productData = tracking?.loadProductData?.() || {};
        return productData[String(productHandle).toUpperCase()] || null;
      }

      setPrivateTrackingProperty(name, value) {
        if (!this.form || value === undefined || value === null || value === "") return;

        const inputName = `properties[${name}]`;
        let input = this.form.querySelector(`[name="${inputName}"]`);
        if (!input) {
          input = document.createElement("input");
          input.type = "hidden";
          input.name = inputName;
          this.form.appendChild(input);
        }

        input.value = String(value);
      }

      syncTrackingAttributionProperties() {
        const productHandle = this.getTrackingProductHandle();
        this.setPrivateTrackingProperty("_gtm_item_id", productHandle.toUpperCase());

        const sizePreference = this.getTrackingSizePreference();
        this.setPrivateTrackingProperty("_gtm_size_preference", sizePreference);

        const attribution = this.getTrackingAttribution();
        if (!attribution) return;

        this.setPrivateTrackingProperty("_gtm_item_list_id", attribution.item_list_id);
        this.setPrivateTrackingProperty("_gtm_item_list_name", attribution.item_list_name);
        this.setPrivateTrackingProperty("_gtm_item_list_index", attribution.index);
      }

      getTrackingSizePreference() {
        const cookieMatch = document.cookie.match(/(?:^|;\s*)wacoal_sizing_pref=([^;]+)/);
        const preference = cookieMatch?.[1] ||
          document.documentElement.getAttribute("data-sizing-pref") || "";

        return String(preference).trim().toUpperCase();
      }

      getTrackingAddToCartLocation() {
        if (this.closest(".style-it-with")) return "style_it_with_pdp";
        if (this.closest("more-in-the-collection")) return "more_in_collection_pdp";
        if (this.closest("product-recommendations")) return "recommended_pdp";
        if (this.closest("recently-viewed-products, .f-recently-viewed")) {
          return "recently_viewed_pdp";
        }
        if (this.closest('product-info[data-section-type="main-product"]')) {
          return "collection_pdp";
        }

        return "";
      }

      validateForm(form) {
        const missingFields = [];
        if (!form) return missingFields;
        const fieldSelectors =
          '[data-product-custom-field] [name][required]:not([hidden]):not([type="hidden"])';
        const requiredFields = form.querySelectorAll(fieldSelectors);
        requiredFields.forEach((field) => {
          field.classList.remove("form-control--warning");
          if (field.type === "radio") {
            const buttons = form.querySelectorAll(
              `input[name="${field.name}"]`
            );
            const selected = Array.from(buttons).some((btn) => btn.checked);
            if (!selected) {
              missingFields.push(field);
              field.classList.add("form-control--warning");
            }
          } else if (!field.value) {
            missingFields.push(field);
            field.classList.add("form-control--warning");
          }
        });
        return missingFields;
      }

      getTrackingColour() {
        const productCard = this.closest(".product-card");
        const selectedSwatch = productCard?.querySelector(
          'color-swatch [aria-pressed="true"][data-value], color-swatch [aria-selected="true"][data-value]'
        );
        if (selectedSwatch?.dataset.value) return selectedSwatch.dataset.value;

        const colourField = this.closest(
          ".f-product-single, .product-quickview, [data-product-container]"
        )?.querySelector(
          '[data-option-name="Color"] input:checked, [data-option-name="Colour"] input:checked, [data-option-name="Finish"] input:checked, [data-option-name="Color"] select, [data-option-name="Colour"] select, [data-option-name="Finish"] select'
        );

        return colourField?.value || this.dataset.gtmColour || "";
      }

      getTrackingVariant() {
        const variantId = String(this.variantIdInput?.value || "");
        const productInfo = this.closest("product-info");
        const currentVariant = productInfo?.currentVariant;
        const productInfoVariant =
          currentVariant && String(currentVariant.id) === variantId
            ? currentVariant
            : productInfo?.productData?.variants?.find(
                (variant) => String(variant.id) === variantId
              );
        if (productInfoVariant) return productInfoVariant;

        const quickAdd = this.closest("quick-add-card");
        if (typeof quickAdd?.getSelectedVariant === "function") {
          return quickAdd.getSelectedVariant();
        }

        return null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.syncTrackingAttributionProperties();
        const missing = this.validateForm(
          this.form.closest(".f-product-single__blocks")
        );

        if (missing && missing.length > 0) {
          return this.Notification.show({
            target: this.domNodes.errorWrapper,
            method: "appendChild",
            type: "warning",
            message: window.FoxThemeStrings.requiredField,
          });
        }

        if (this.submitButton.classList.contains("btn--loading")) return;

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("btn--loading");

        const config = {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };

        this.closest("quick-add-card")?.syncRegionalSizeProperties?.();
        window.WacoalRegionalSizeToggle?.updateProperties?.(this.form);
        const formData = new FormData(this.form);
        const trackingColour = this.getTrackingColour();
        const trackingSourceLocation = this.getTrackingAddToCartLocation();
        const trackingVariant = this.getTrackingVariant();
        const compareAtPrice = Number(trackingVariant?.compare_at_price) || 0;
        const variantPrice = Number(trackingVariant?.price) || 0;

        // Private line item property used by Shopify Customer Events. Checkout
        // pixel payloads contain SKU and size, but not the product colour
        // metafield needed for GA4's item_variant parameter.
        if (trackingColour) {
          formData.set("properties[_gtm_colour]", trackingColour);
        }

        if (compareAtPrice > variantPrice) {
          formData.set("properties[_gtm_compare_at_price]", compareAtPrice);
        }

        let sectionToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", {
            bubbles: true,
            detail: { sections: sectionToBundle },
          })
        );

        if (this.cartDrawer) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.cartDrawer
              .getSectionsToRender()
              .map((section) => section.id),
          ];
        }

        if (this.mainCart) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.mainCart
              .getSectionsToRender()
              .map((section) => section.section),
          ];
        }

        formData.append("sections", sectionToBundle);

        if (this.mainCart || this.cartDrawer) {
          formData.append("sections_url", window.location.pathname);
        }

        config.body = formData;
        const { FoxThemeSettings, FoxThemeStrings } = window;
        fetch(`${FoxThemeSettings.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.description,
                message: response.message,
              });

              // Dispatch event for third-party
              document.dispatchEvent(
                new CustomEvent("product-ajax:error", {
                  detail: {
                    errorMessage: response.description,
                  },
                })
              );

              this.error = true;
              const notificationTarget = this.notificationType === "toast" ? document.body : this.errorWrapper;

              return this.Notification.show({
                target: notificationTarget,
                method: "appendChild",
                type: "warning",
                message: response.description || response.message,
                last: 3000,
                sticky: this.notificationType === "toast",
              });
            }

            if (
              !FoxThemeSettings.cart.openDrawerWhenAdded ||
              FoxThemeSettings.cart.cartType === "page"
            ) {
              this.Notification.show({
                target: this.errorWrapper ? this.errorWrapper : document.body,
                method: "appendChild",
                type: "success",
                message: FoxThemeStrings.item_added,
                last: 3000,
                sticky: this.notificationType === "toast",
              });
            }
            if (FoxThemeSettings.cart.cartType === "page") {
              FoxThemeCartHelpers.getState().then((state) => {
                FoxThemeCartHelpers.updateCartCount(state.item_count);
              });
            }

            this.error = false;
            window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, response);

            // DispatchEvent for third-party
            document.dispatchEvent(
              new CustomEvent("product-ajax:added", {
                detail: {
                  product: response,
                  tracking: {
                    colour: trackingColour,
                    sourceLocation: trackingSourceLocation,
                    compareAtPrice,
                    productHandle: this.getTrackingProductHandle(),
                  },
                },
              })
            );
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("btn--loading");
            this.submitButton.removeAttribute("aria-disabled");
          });
      }
    }
  );
}
